// core/guards/auth.guard.ts
import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Routing, UserType } from '@medicalapp/shared-core';
import { AuthService } from '@core/services/auth/auth.service';
import { CurrentUserService } from '@core/services/common/current-user.service';
import { PermissionService } from '@core/services/common/permission.service';
import { PatientLegalAcceptanceService } from '@services/patient/patient-legal-acceptance.service';
import { firstValueFrom } from 'rxjs';

export const authGuard: CanActivateFn = async (route, state): Promise<boolean | UrlTree> => {
  const authService        = inject(AuthService);
  const router             = inject(Router);
  const currentUserService = inject(CurrentUserService);
  const permissionService  = inject(PermissionService);
  const legalService       = inject(PatientLegalAcceptanceService);

  // Skip public routes
  if (state.url.includes(`/${Routing.Auth}/`)) return true;

  await currentUserService.getAsync();

  const isAuthenticated = authService.isAuthenticated();
  const currentUser     = currentUserService.get();

  if (!isAuthenticated || !currentUser) {
    await currentUserService.clearSession();
    return router.createUrlTree([`/${Routing.Auth}/${Routing.Login}`], {
      queryParams: { returnUrl: state.url },
    });
  }

  // Check if destination is the Accept Terms page
  const cleanUrl = state.url.split('?')[0].split('#')[0];
  const urlSegments = cleanUrl.split('/').filter(Boolean);
  const isAcceptTerms = urlSegments.includes(Routing.AcceptTerms);

  if (isAcceptTerms) {
    if (currentUser.userTypeId !== UserType.Patient) {
      return router.createUrlTree([`/${Routing.Dashboard}`]);
    }
    // Allow patient to access accept-terms directly without permission or legal loop check
    return true;
  }

  // Permission check
  if (currentUser.userTypeId === UserType.Patient) {
    const isAccessible = permissionService.checkAppModuleAccess(state.url);
    if (!isAccessible) {
      return router.createUrlTree([`/${Routing.Forbidden}`]);
    }
  }

  // Legal check — only patients
  if (currentUser.userTypeId === UserType.Patient) {
    try {
      const hasPending = await firstValueFrom(legalService.checkPendingAcceptance());
      if (hasPending) {
        return router.createUrlTree([`/${Routing.AcceptTerms}`]);
      }
    } catch {
      // Fail open
    }
  }

  return true;
};